import type { ReactNode } from "react";
import { box, HandSvg, ink, line, ln, mark, markFill, on, onFill, we, wp, type HandPart } from "@/components/hand-icon";
import { NAV_ICON_PATHS } from "@/components/NavIcon";
import { PRICING_ICON_PATHS } from "@/components/PricingIcon";

/*
  開発演習 LP と /women の FAQ・講師・流れで使う図記号 (2026-10-08 ユーザーの指示「この icon が AI っぽいから独自 SVG にして」)。
  既製のアイコンの形はなぞらず、このページの題材の物 (本番のアドレスバー、PR の枝、レビューの吹き出し、経歴書の1行、チケット) で描く。
  描き方は @/components/hand-icon の「B 手の揺れの太線」(2026-10-12 オーナーが選んだ)。線は currentColor の 2.6、黄は閉じた形 1 つだけ、
  黄の上の印は #0E0E10。28〜40px で使う前提。
  Mark で終わる小さな印 (チェック、矢印など) は 10〜16px の見本の中で使うので、黄を持たない
*/

export type IconProps = { size?: number; className?: string };
export type PdIcon = (p: IconProps) => ReactNode;

function icon(parts: HandPart[]): PdIcon {
  return function PdHandIcon({ size = 32, className }: IconProps) {
    return <HandSvg parts={parts} size={size} className={className} />;
  };
}

const open = (pts: Parameters<typeof wp>[0], r = 2.5) => wp(pts, { closed: false, r });

/** 本番のURL。右端を破ったアドレスバーの切れ端と、公開中の点 */
export const UrlIcon = icon([
  mark(
    wp(
      [
        [2.6, 7.4, 3.6],
        [20.8, 7.4, 0.4],
        [19.2, 9.5, 0.4],
        [20.8, 11.5, 0.4],
        [19.2, 13.5, 0.4],
        [20.8, 15.6, 0.4],
        [2.6, 15.6, 3.6],
      ],
      { r: 0.4 },
    ),
  ),
  on("M6.8 11.5h.01", 3),
  on(ln(10.2, 11.5, 15, 11.5), 2.4),
]);

/** 作ったものを1枚で見せる公開ページ。窓の中に作品の札と説明の2行 */
export const PortfolioIcon = icon([
  box(2.8, 3.6, 18.4, 16.8),
  ln(3, 8.4, 21, 8.4),
  mark(box(6.4, 11.6, 5, 5.4, 1)),
  ln(15, 12.4, 17.6, 12.4) + ln(15, 16.2, 16.6, 16.2),
]);

/** 職務経歴書。罫線の行のうち、開発実績の1行だけが黄 */
export const ResumeIcon = icon([
  box(4.6, 2.6, 14.8, 18.8, 2),
  ln(8.4, 6.6, 15.6, 6.6) + ln(8.4, 17.6, 13, 17.6),
  mark(box(8.4, 10.4, 7.2, 3.4, 1)),
]);

/** レビューのコメント。行番号の溝を持つ黄の吹き出し */
export const ReviewIcon = icon([
  mark(
    wp([
      [2.8, 4],
      [21.2, 4],
      [21.2, 16.2],
      [12, 16.2, 0.6],
      [7.2, 20.4, 0.5],
      [7.6, 16.2, 0.6],
      [2.8, 16.2],
    ]),
  ),
  on(ln(7.2, 7.8, 7.2, 12.4, 0.3), 2),
  on(ln(10.6, 8, 17.4, 8) + ln(10.6, 12.2, 15.4, 12.2), 2.4),
]);

/** 面接。問いの吹き出しに、黄の答えの吹き出しが返る */
export const InterviewIcon = icon(NAV_ICON_PATHS.faq);

/** エンジニアとして入社する。首から下げる紐の付いた社員証に、黄の顔写真 */
export const BadgeIcon = icon([
  ln(12, 2.4, 12, 6.2, 0.2),
  box(2.6, 8, 18.8, 13, 2.4),
  mark(we(8, 14.5, 2.3)),
  ln(13, 12.6, 18, 12.6) + ln(13, 16.4, 16.2, 16.4),
]);

/** フリーランスの仕事。角を折った契約書と、押した黄の判 */
export const ContractIcon = icon([
  open([
    [18.4, 11.4],
    [18.4, 7.4, 0.6],
    [14, 3, 0.6],
    [5.6, 3],
    [5.6, 21],
    [10.4, 21],
  ]),
  open(
    [
      [14, 3.4],
      [14, 7.4],
      [18, 7.4],
    ],
    0.8,
  ),
  ln(8.8, 10.6, 13.6, 10.6) + ln(8.8, 14.4, 11.2, 14.4),
  mark(we(16, 17.6, 3)),
]);

/** 実務と同じ開発の流れ。3列の看板の上を、黄のチケットが1枚ずつ進む */
export const BoardIcon = icon([
  box(2.8, 3.8, 18.4, 16.4),
  "M9 4.6v14.8M15 4.6v14.8",
  ink(box(4.6, 7, 2.8, 2.6, 0.6) + box(4.6, 11.4, 2.8, 2.6, 0.6) + box(16.6, 7, 2.8, 2.6, 0.6)),
  markFill(box(10.4, 10.2, 3.2, 3.6, 0.6)),
]);

/** 要件定義。クリップボードに、誰が・何を・なぜの3行の欄。留め具が黄 */
export const SpecIcon = icon([
  open([
    [16.2, 4.4],
    [19.6, 4.4],
    [19.6, 21.2],
    [4.4, 21.2],
    [4.4, 4.4],
    [7.8, 4.4],
  ]),
  mark(box(8.4, 2.2, 7.2, 4.6, 1.2)),
  "M8.4 10.6h.6M8.4 14.4h.6M8.4 18h.6",
  ln(12, 10.6, 15.8, 10.6) + ln(12, 14.4, 15.8, 14.4) + ln(12, 18, 14.4, 18),
]);

/** AIを使った開発。書きかけの行の先に、黄の提案が出る */
export const SuggestIcon = icon([
  ln(3, 6, 15, 6) + ln(3, 12, 7, 12) + ln(3, 18, 11.4, 18),
  line(ln(10.4, 9.6, 10.4, 14.4, 0.2), 2),
  mark(box(13.6, 8.8, 7.8, 6.4, 2)),
  on("M16 12h.01M19 12h.01", 2.6),
]);

/** 本番サービスの GitHub。main から枝を切り、黄のコミットを積んで、PR で main へ戻す */
export const BranchIcon = icon([
  ln(6, 2.8, 6, 21.2),
  "M17.6 10.6c0 4.4-3.6 6.4-10.2 7.6",
  mark(we(17.6, 6.4, 2.6)),
]);

/** 文字で伝える力。週の報告の投稿で、名前の下に、済んだことと次にやることの行 */
export const ReportIcon = icon([
  box(2.8, 3.2, 18.4, 17.6),
  mark(we(7.4, 8.2, 2)),
  ln(11.6, 8.2, 17.4, 8.2),
  line("m6 13.6 1.3 1.3 2.1-2.4" + ln(12, 13.6, 17.4, 13.6) + ln(6.4, 17.4, 9, 17.4) + ln(12, 17.4, 15.4, 17.4), 2.2),
]);

/** 受講生どうしの名簿。顔が縦に3つ並び、自分の1つが黄 */
export const RosterIcon = icon([
  box(3.6, 2.6, 16.8, 18.8),
  ink(we(8.4, 7.2, 1.5) + we(8.4, 16.8, 1.5)),
  mark(we(8.4, 12, 1.4)),
  ln(12.4, 7.2, 16.6, 7.2) + ln(12.4, 12, 16.6, 12) + ln(12.4, 16.8, 15, 16.8),
]);

/** 月1回の集まり、期間。めくる暦の1日だけを黄の丸で囲む */
export const CalendarIcon = icon([
  box(3, 5, 18, 15.8),
  "M8 2.6v3.6M16 2.6v3.6",
  ln(3.2, 9.8, 20.8, 9.8),
  "M7.4 13.6h.01M11.2 13.6h.01M7.4 17.4h.01M11.2 17.4h.01",
  mark(we(15.6, 15.4, 2.2)),
]);

/** 教材の課題と本番の開発の違い。左に丸をもらった課題の紙、右に黄の本番のアドレスバー */
export const VersusIcon = icon([
  box(2.4, 4.6, 8.2, 14.8, 1.4),
  line(
    open(
      [
        [4.4, 12],
        [5.9, 13.6],
        [8.4, 10.4],
      ],
      0.6,
    ),
    2,
  ),
  mark(box(13, 9, 8.6, 6, 3)),
  on("M15.8 12h.01", 2.8),
]);

/** 既存のコードを読む。行番号の溝と字下げの行で、読んでいる1行が黄 */
export const CodeIcon = icon([
  box(2.8, 3.8, 18.4, 16.4),
  line(ln(7.6, 4.6, 7.6, 19.4, 0.4), 2),
  ln(10.8, 7.8, 15.6, 7.8) + ln(12.8, 16.2, 16.4, 16.2),
  markFill(box(10, 10.2, 9.4, 3.6, 1)),
  on(ln(12.2, 12, 17, 12), 2.2),
]);

/** 人の設計に合わせる。寸法線の入った図面の枠に、黄の部品を1つはめる */
export const BlueprintIcon = icon([
  line("M7.4 2.8h12.4M7.4 1.8v2M19.8 1.8v2M2.8 7.4v12.4M1.8 7.4h2M1.8 19.8h2", 1.8),
  box(7, 7, 13.6, 13.6, 1.4),
  mark(box(11.4, 11.6, 5, 5, 1)),
]);

/** 壊さないように出す。検査の印が3つそろってから、黄の本番のアドレスバーへ */
export const SafeShipIcon = icon([
  line("m2.8 7.6 1.5 1.5 2.7-2.9M10 7.6l1.5 1.5 2.7-2.9M17.2 7.6l1.5 1.5 2.7-2.9", 2.2),
  mark(box(2.8, 13.2, 18.4, 7, 3.4)),
  on("M6.4 16.7h.01", 2.8),
  on(ln(10, 16.7, 16.4, 16.7), 2.4),
]);

/** 運用。本番の画面の中に、動き続けていることを示す脈の線。上の帯が黄 */
export const OperateIcon = icon([
  markFill("M5.3 3.8h13.4c1.4 0 2.5 1.1 2.5 2.5v2.3H2.8V6.3c0-1.4 1.1-2.5 2.5-2.5z"),
  box(2.8, 3.8, 18.4, 16.6),
  ln(2.8, 8.6, 21.2, 8.6),
  open(
    [
      [6, 14.6],
      [8.8, 14.6],
      [10.6, 11.4],
      [13.4, 17.6],
      [15.2, 14.6],
      [18, 14.6],
    ],
    0.6,
  ),
]);

/** 料金。穴の空いた黄の値札に円の字 */
export const PriceIcon = icon([
  ...NAV_ICON_PATHS.cost,
  on("m11.4 11.6 1.7 2.1 1.7-2.1M13.1 13.7v3.4M11.6 14.6h3M11.6 16.2h3", 1.8),
]);

/** 参加基準。コースの進み具合の帯と、いまいる所の黄の印 (学習の途中でも参加できる) */
export const ProgressIcon = icon([
  box(2.6, 12.6, 18.8, 6.6, 3.3),
  ink("M5.9 12.6H12v6.6H5.9a3.3 3.3 0 0 1 0-6.6z"),
  mark(
    wp(
      [
        [8.6, 3.8],
        [15.4, 3.8],
        [12, 8.8],
      ],
      { r: 1 },
    ),
  ),
]);

/** 進め方。切り取り線のある黄のチケット。Asana に用意したチケットを1枚ずつ進める */
export const TicketIcon = icon([
  mark(
    "M4.4 5.8q7.6-.8 15.2 0c.9 0 1.6.7 1.6 1.6V10a2 2 0 0 0 0 4v2.6c0 .9-.7 1.6-1.6 1.6q-7.6.8-15.2 0c-.9 0-1.6-.7-1.6-1.6V14a2 2 0 0 0 0-4V7.4c0-.9.7-1.6 1.6-1.6z",
  ),
  on("M15.4 8.2v.6M15.4 11.7v.6M15.4 15.2v.6", 2),
  on(ln(7, 10.4, 11.6, 10.4) + ln(7, 13.8, 9.8, 13.8), 2.4),
]);

/** 教材の課題。答えの升に黄の丸がついた問題用紙 */
export const WorkbookIcon = icon([
  box(4, 2.6, 16, 18.8, 2),
  mark(box(7.4, 6.2, 4.2, 4.2, 0.8)),
  box(7.4, 13.6, 4.2, 4.2, 0.8),
  ln(14.6, 8.3, 16.8, 8.3) + ln(14.6, 15.7, 16.8, 15.7),
]);

/** 教科書 (読みもの)。開いた 1 冊。右のページに黄の図、左のページに字の行 */
export const TextbookIcon = icon([
  box(3, 4.6, 8.6, 14.8, 1.6),
  box(12.4, 4.6, 8.6, 14.8, 1.6),
  mark(box(14.8, 8, 4, 3.6, 0.8)),
  ln(5.4, 8.6, 9.2, 8.6) + ln(5.4, 12, 9.2, 12) + ln(5.4, 15.4, 9.2, 15.4) + ln(14.8, 15.4, 18.6, 15.4),
]);

/** その先も。竿に結んだ黄の三角の旗 */
export const FlagMark = icon([
  ln(5, 21, 5, 3),
  mark(
    wp(
      [
        [5, 4],
        [18, 4],
        [14.8, 8],
        [18, 12],
        [5, 12],
      ],
      { r: 0.8 },
    ),
  ),
]);

/** オンラインの集まり。画面に映る1人の顔 (暦の青い丸の中で白抜きで使う) */
export const CallMark = icon([
  box(2.8, 4, 18.4, 13.6, 2),
  we(12, 9, 2),
  "M8 17.4c.6-2.2 2.1-3.4 4-3.4s3.4 1.2 4 3.4",
  ln(8.6, 21, 15.4, 21),
]);

/** 平日毎晩のもくもく作業会。ノート PC の画面に黄の三日月 (夜に手を動かす時間) */
export const MokumokuIcon = icon([
  box(2.6, 3.6, 18.8, 13.6),
  ln(1.2, 20.8, 22.8, 20.8),
  mark("M13.4 6.4Q9 6.8 8.8 10.6Q8.6 14.4 12.6 14.4Q14.8 14.4 15.8 12.8Q12.8 12.6 12.4 10.2Q12.2 8 13.4 6.4Z"),
]);

/** 構成図 ブラウザ。黄の窓とアドレス欄 */
export const BrowserIcon = icon([
  mark(box(2.8, 4.4, 18.4, 15.2)),
  on(ln(3.2, 9.4, 20.8, 9.4, 0.5), 2.4),
  on("M6.2 6.9h.01M9 6.9h.01", 2.4),
  on(ln(7, 14.6, 17, 14.6), 2.4),
]);

/** 構成図 スマホのアプリ。縦長の黄の端末と、下のホームバー */
export const MobileAppIcon = icon([mark(box(6.6, 2.4, 10.8, 19.2, 2.6)), on(ln(10.4, 5.6, 13.6, 5.6) + ln(10.4, 18.4, 13.6, 18.4), 2.4)]);

/** 構成図 エッジ。黄の地球に、四隅の配信の点 */
export const EdgeIcon = icon([
  mark(we(12, 12, 8.2)),
  on(ln(4.2, 12, 19.8, 12, 0.5) + "M12 4c-3.2 3-3.2 13 0 16M12 4c3.2 3 3.2 13 0 16", 2),
  "M2.6 2.6h.01M21.4 2.6h.01M2.6 21.4h.01M21.4 21.4h.01",
]);

/** 構成図 API サーバー。2段のサーバーの棚で、下の段が黄 */
export const ServerIcon = icon([
  box(2.8, 3, 18.4, 7.4, 2),
  "M6.6 6.7h.01" + ln(11.4, 6.7, 17.4, 6.7),
  mark(box(2.8, 13.6, 18.4, 7.4, 2)),
  on("M6.6 17.3h.01" + ln(11.4, 17.3, 17.4, 17.3), 2.6),
]);

/** 構成図 データベース。円柱で、ふたの面が黄 */
export const DatabaseIcon = icon([
  "M4 6.4v11.4c0 1.8 3.6 3.2 8 3.2s8-1.4 8-3.2V6.4",
  "M4 12.2c0 1.8 3.6 3.2 8 3.2s8-1.4 8-3.2",
  mark(we(12, 6.2, 8, 3, 0)),
]);

/** 構成図 コード実行。点線の箱の中に黄の鍵、隔離の意味 */
export const SandboxIcon = icon([
  line(box(2.4, 2.4, 19.2, 19.2, 3), 2.2, "3 2.8"),
  "M9.6 10.6V9a2.4 2.4 0 0 1 4.8 0v1.6",
  mark(box(7, 10.6, 10, 7.8, 1.8)),
  on("M12 14.5h.01", 3),
]);

/** 構成図 Docker。積み上げたコンテナで、上の1つが黄 */
export const ContainerIcon = icon([
  box(2.6, 12.4, 18.8, 8.6, 1.4),
  "M8 15.2v3M12 15.2v3M16 15.2v3",
  mark(box(6.6, 3, 10.8, 6.4, 1.4)),
  on("M10.6 5.4v1.6M13.4 5.4v1.6", 2),
]);

/** 構成図 AI チューター。黄の吹き出しの中の火花 */
export const TutorIcon = icon([
  mark(
    wp([
      [2.8, 4],
      [21.2, 4],
      [21.2, 16.2],
      [12.6, 16.2, 0.6],
      [7.8, 20.4, 0.5],
      [8.2, 16.2, 0.6],
      [2.8, 16.2],
    ]),
  ),
  onFill("M12 5.8c.5 2.8 1.2 3.6 4 4.2-2.8.6-3.5 1.4-4 4.2-.5-2.8-1.2-3.6-4-4.2 2.8-.6 3.5-1.4 4-4.2z"),
]);

// ここから下は 10〜16px の見本の中で使う小さな印。黄は持たない
export const CheckMark = icon([
  open(
    [
      [4.4, 12.6],
      [9.4, 17.4],
      [19.6, 6.8],
    ],
    1,
  ),
]);

export const CrossMark = icon([ln(6.5, 6.5, 17.5, 17.5) + ln(17.5, 6.5, 6.5, 17.5)]);

export const PlusMark = icon([line(ln(12, 4.5, 12, 19.5) + ln(4.5, 12, 19.5, 12), 3)]);

export const ArrowMark = icon([
  "M3.5 12.5c4.5-.6 9.5-.7 15.5-.5",
  open(
    [
      [13.5, 6.5],
      [19, 12],
      [13.5, 17.5],
    ],
    1,
  ),
]);

export const AlertMark = icon([line(ln(12, 4, 12, 14, 0.4), 3), line("M12 19.5h.01", 3.4)]);

export const PersonMark = icon([we(12, 8.4, 3.6), "M5 20.5c1-4 3.8-6 7-6s6 2 7 6"]);

/** 経歴書の切り抜きの、はさみを入れる点線と切り込み */
export const CutMark = icon([
  line(ln(2, 14, 22, 14, 0.3), 2.2, "3 2.6"),
  open(
    [
      [9, 7.5],
      [12, 14],
      [15, 7.5],
    ],
    0.8,
  ),
]);

/** 大学。学位帽。HP の講師の経歴の行の頭と、入学の段で使う */
export const GradCapIcon = icon(PRICING_ICON_PATHS.graduate);

/** 今の仕事。取っ手の付いた黄の仕事かばんと、留め金の帯 */
export const BriefcaseIcon = icon(PRICING_ICON_PATHS.briefcase);

/** 資格。合格の証書と、角に押した黄の丸い印 */
export const CertificateIcon = icon([
  open([
    [12.2, 20],
    [3, 20],
    [3, 4],
    [21, 4],
    [21, 10.8],
  ]),
  ln(7, 8.4, 17, 8.4) + ln(7, 12.2, 11, 12.2),
  line("m14.8 18.4-.8 3.2 2.6-1.3 2.6 1.3-.8-3.2", 2),
  mark(we(16.6, 15.2, 3)),
]);

/** これまでの実績。台の上の黄の優勝杯 */
export const TrophyIcon = icon([
  mark("M7 3.6q5-.5 10 0v5.2a5 5 0 0 1-10 0z"),
  "M7 5.8H4.6v1.4a3 3 0 0 0 2.8 3M17 5.8h2.4v1.4a3 3 0 0 1-2.8 3",
  ln(12, 15.6, 12, 17.4),
  ln(7.6, 20.8, 16.4, 20.8),
]);

/** 個別オンボーディング。チェックを3つ並べた黄の確認の用紙。ツールの準備と進め方の確認の段で使う */
export const ChecklistIcon = icon([
  mark(box(4, 3, 16, 18.6, 2.5)),
  on("m7.4 7.8 1.3 1.3 2.2-2.5M7.4 12.6l1.3 1.3 2.2-2.5M7.4 17.4l1.3 1.3 2.2-2.5", 1.8),
  on("M13.6 8h3.2M13.6 12.8h3.2M13.6 17.6h3.2", 2.2),
]);

/** 正式に学習開始。竿に掲げた黄の旗に、足元の一本線を付けた形 */
export const StartFlagIcon = icon([
  ln(6, 21, 6, 3),
  ln(3.4, 21, 8.6, 21),
  mark(
    wp(
      [
        [6, 4],
        [19, 4],
        [15.8, 8.2],
        [19, 12.4],
        [6, 12.4],
      ],
      { r: 0.8 },
    ),
  ),
]);
